import { observer } from 'mobx-react-lite';
import {
  useEffect,
  useRef,
  type FocusEvent,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';

import { electronApis } from '@/features/electron-api';
import { FEATURES, useFeature } from '@/features/feature-flags';
import { logError } from '@/lib/errorLogging';
import { cn } from '@/lib/utils';
import { inElectron } from '@/utils/capabilities';

import { agentRunsStore } from './agent-runs.store';
import {
  handleLocalTerminalKey,
  isInsideTerminalDock,
  LOCAL_TERMINAL_DOCK_TEST_ID,
  setTerminalKeyboardOwner,
  terminalOwnsKeyboard,
} from './local-terminal-keys';
import { LocalTerminal } from './LocalTerminal';
import { LocalTerminalTabs } from './LocalTerminalTabs';
import { sandboxTerminalPanelStore } from './sandbox-terminal-panel.store';
import { TerminalDock } from './TerminalDock';
import { TerminalErrorBoundary } from './TerminalErrorBoundary';

/**
 * App-wide bottom dock for the local terminal. Mounted once in MainLayout over the
 * main content column, so it is reachable from every route (including Home).
 *
 * Every tab's terminal stays mounted while the tab exists; inactive tabs and the
 * hidden dock are display:none, so shells, scrollback and connections survive
 * tab switches and ctrl+`. Only closing a tab (X, cmd+w, shell exit) unmounts it.
 */
export const LocalTerminalPanel = observer(function LocalTerminalPanel() {
  const hasFeature = useFeature();
  const enabled = inElectron() && hasFeature(FEATURES.LOCAL_TERMINAL);
  const { tabs, activeKey, activeTab, isLocalOpen, localMounted } =
    sandboxTerminalPanelStore;
  const mounted = enabled && localMounted;
  const visible = mounted && isLocalOpen;
  const activeSessionId = activeTab?.sessionId ?? null;

  // Agent-run commands (TERMINAL_RUN) are headless host sessions: no tab opens on
  // its own. Track start/finish so the chat card for the tool call can show the
  // output and offer to attach a tab (adoptSession). Subscribed for the panel's
  // whole life, not just while a tab is mounted: runs arrive with the dock closed.
  useEffect(() => {
    if (!enabled) return;
    const unsubOpened = electronApis.localTerminal.onSessionOpened((event) =>
      agentRunsStore.start(event)
    );
    const unsubClosed = electronApis.localTerminal.onSessionClosed((result) =>
      agentRunsStore.finish(result)
    );
    return () => {
      unsubOpened();
      unsubClosed();
    };
    // On HMR of the store module this component module re-evaluates too and Fast
    // Refresh re-runs the effect, so the subscription moves to the new instance.
  }, [enabled]);

  // Main uses the active tab's shell as the default cwd for agent-run commands.
  useEffect(() => {
    if (!enabled) return;
    electronApis.localTerminal.setActiveSession(activeSessionId);
  }, [enabled, activeSessionId]);

  // Remembered tabs may point at sessions the host no longer has (main restarted;
  // reaped). Ask once at startup and drop the stale ones before they connect and
  // silently become fresh shells under the old title.
  // Kill path for tabs with no live socket (see store.requestKill).
  useEffect(() => {
    sandboxTerminalPanelStore.setSessionKiller((id) => {
      void electronApis.localTerminal.killSession(id);
    });
    return () => sandboxTerminalPanelStore.setSessionKiller(null);
  }, []);

  useEffect(() => {
    if (!enabled || sandboxTerminalPanelStore.tabs.length === 0) return;
    let cancelled = false;
    electronApis.localTerminal
      .listSessions()
      .then((live) => {
        if (!cancelled) {
          sandboxTerminalPanelStore.reconcile(live.map((s) => s.id));
        }
      })
      .catch((error) => {
        logError(error, 'Terminal session reconcile failed', {
          context: 'LocalTerminalPanel',
        });
        // can't tell what's live; drop everything rather than resurrect ghosts
        if (!cancelled) sandboxTerminalPanelStore.reconcile([]);
      });
    return () => {
      cancelled = true;
    };
    // startup only
  }, [enabled]);

  // Focus the active shell whenever the dock or the active tab becomes visible
  // and an intent is pending (see TerminalFocusIntent). Has to run after commit:
  // while hidden the xterm textarea is display:none. If the tab's terminal is
  // not mounted yet the intent stays put and registerLocalFocus consumes it.
  useEffect(() => {
    if (visible) sandboxTerminalPanelStore.consumeFocusIntent();
  }, [visible, activeKey]);

  // When the dock hides (ctrl+`, last tab closed) and it had the keyboard, give
  // focus back to the chat composer so the user can keep typing without a
  // click. If focus was already somewhere else, leave it alone.
  const wasVisible = useRef(visible);
  useEffect(() => {
    const hadFocus =
      wasVisible.current &&
      !visible &&
      (terminalOwnsKeyboard() || document.activeElement === document.body);
    wasVisible.current = visible;
    if (!hadFocus) return;
    // rAF: the dock's display:none has to land first, or the composer's own
    // blur handling sees a focus target that is about to disappear.
    const frame = requestAnimationFrame(() => {
      const composer = document.querySelector<HTMLElement>(
        '[data-testid="chat-input"]'
      );
      composer?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [visible]);

  // Tab-shortcut ownership (see local-terminal-keys.ts). Focus events inside the
  // dock claim it (below); a click anywhere decides by where it landed, which is
  // the only signal when focus goes to body (clicking chat text, a tab swap that
  // hides the focused textarea). Hiding the dock releases it.
  useEffect(() => {
    if (!visible) {
      setTerminalKeyboardOwner(false);
      return;
    }
    const onPointerDown = (event: PointerEvent) =>
      setTerminalKeyboardOwner(isInsideTerminalDock(event.target));
    // Chords that land outside the dock while it owns the keyboard (focus fell
    // to body). Inside the dock xterm / the wrapper below handle them. Must
    // preventDefault: with nobody claiming cmd+w, the macOS menu's "Close
    // Window" accelerator fires and hides the whole app.
    const onKeyDown = (event: KeyboardEvent) => {
      if (!terminalOwnsKeyboard() || isInsideTerminalDock(event.target)) return;
      if (handleLocalTerminalKey(event)) {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    document.addEventListener('pointerdown', onPointerDown, { capture: true });
    document.addEventListener('keydown', onKeyDown, { capture: true });
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, {
        capture: true,
      });
      document.removeEventListener('keydown', onKeyDown, { capture: true });
      setTerminalKeyboardOwner(false);
    };
  }, [visible]);

  if (!mounted) return null;

  const onFocusCapture = () => setTerminalKeyboardOwner(true);
  const onBlurCapture = (event: FocusEvent) => {
    // relatedTarget null = focus went nowhere (body); keep ownership, the dock is
    // still what the user is working in. An element outside the dock releases it.
    if (event.relatedTarget && !isInsideTerminalDock(event.relatedTarget)) {
      setTerminalKeyboardOwner(false);
    }
  };
  // Tab chords pressed inside the dock but outside xterm (tab strip, find bar).
  // xterm handles and stops its own, so nothing reaches here twice.
  const onKeyDown = (event: ReactKeyboardEvent) => {
    if (handleLocalTerminalKey(event.nativeEvent)) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  return (
    <TerminalDock
      overlay={false}
      hidden={!visible}
      testId={LOCAL_TERMINAL_DOCK_TEST_ID}
    >
      <div
        className="h-full w-full"
        onFocusCapture={onFocusCapture}
        onBlurCapture={onBlurCapture}
        onKeyDown={onKeyDown}
      >
        {tabs.map((tab) => (
          <div
            key={tab.key}
            className={cn('h-full w-full', tab.key !== activeKey && 'hidden')}
            data-terminal-tab={tab.key}
          >
            {/* One boundary per tab: a crash closes that tab only. The boundary
              unmounts with the tab, so a later tab starts with a clean one
              (a class boundary never leaves its failed state on its own). */}
            <TerminalErrorBoundary
              // through requestKill, so the shell dies with the tab
              onError={() => sandboxTerminalPanelStore.requestKill(tab.key)}
            >
              <LocalTerminal tabKey={tab.key} header={<LocalTerminalTabs />} />
            </TerminalErrorBoundary>
          </div>
        ))}
      </div>
    </TerminalDock>
  );
});
